import { useState, useRef, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import Papa from "papaparse";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Upload, Download, FileSpreadsheet, Loader2, CheckCircle2, AlertTriangle, XCircle } from "lucide-react";
import type { CustomerCategory } from "@/hooks/useCustomerCategories";

interface Props {
  open: boolean;
  onClose: () => void;
  organizationId: string;
  categories: CustomerCategory[];
}

interface ParsedRow {
  _rowNum: number;
  full_name?: string;
  email?: string;
  phone?: string;
  invoice_email?: string;
  level?: string;
  category?: string;
  notes?: string;
}

const FIELD_OPTIONS = [
  { value: "skip", label: "— Hoppa över —" },
  { value: "full_name", label: "Namn" },
  { value: "email", label: "E-post" },
  { value: "phone", label: "Telefon" },
  { value: "invoice_email", label: "Fakturamail" },
  { value: "level", label: "Nivå (1-10)" },
  { value: "category", label: "Kundkategori" },
  { value: "notes", label: "Anteckningar" },
];

function autoMapColumn(header: string): string {
  const h = header.toLowerCase().trim();
  if (["namn", "name", "full_name", "fullname", "förnamn", "fullständigt namn"].includes(h)) return "full_name";
  if (["email", "e-post", "epost", "mail"].includes(h)) return "email";
  if (["telefon", "tel", "phone", "mobil", "mobilnummer"].includes(h)) return "phone";
  if (["fakturamail", "invoice_email", "faktura", "faktura e-post"].includes(h)) return "invoice_email";
  if (["nivå", "level", "niva"].includes(h)) return "level";
  if (["kategori", "category", "kundkategori"].includes(h)) return "category";
  if (["anteckning", "notes", "kommentar", "anteckningar"].includes(h)) return "notes";
  return "skip";
}

function downloadTemplate() {
  const header = "namn,email,telefon,fakturamail,nivå,kategori\n";
  const example = "Anna Svensson,anna@example.com,0701234567,faktura@foretag.se,5,Medlem\nErik Johansson,erik@example.com,0709876543,,3,Standard\n";
  const blob = new Blob(["\uFEFF" + header + example], { type: "text/csv;charset=utf-8;" });
  const a = document.createElement("a");
  a.href = URL.createObjectURL(blob);
  a.download = "kundimport_mall.csv";
  a.click();
}

export default function CustomerImportDialog({ open, onClose, organizationId, categories }: Props) {
  const { t } = useTranslation("admin");
  const qc = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [step, setStep] = useState<1 | 2 | 3>(1);
  const [rawData, setRawData] = useState<string[][]>([]);
  const [headers, setHeaders] = useState<string[]>([]);
  const [mapping, setMapping] = useState<Record<number, string>>({});
  const [fileName, setFileName] = useState("");
  const [importResult, setImportResult] = useState<{ imported: number; skipped: number; errors: string[] } | null>(null);

  const resetState = () => {
    setStep(1);
    setRawData([]);
    setHeaders([]);
    setMapping({});
    setFileName("");
    setImportResult(null);
  };

  const handleFile = (file?: File | null) => {
    if (!file) return;
    setFileName(file.name);
    Papa.parse(file, {
      skipEmptyLines: true,
      complete: (result) => {
        const rows = result.data as string[][];
        if (rows.length < 2) {
          toast.error(t("admin:toast.csvEmpty"));
          return;
        }
        const hdrs = rows[0];
        setHeaders(hdrs);
        setRawData(rows.slice(1));
        // Auto-map
        const m: Record<number, string> = {};
        hdrs.forEach((h, i) => { m[i] = autoMapColumn(h); });
        setMapping(m);
        setStep(2);
      },
      error: () => toast.error(t("admin:toast.csvReadError")),
    });
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    handleFile(e.dataTransfer.files?.[0]);
  };

  // Build parsed rows from mapping
  const parsedRows = useMemo<ParsedRow[]>(() => {
    return rawData.map((row, idx) => {
      const parsed: ParsedRow = { _rowNum: idx + 2 };
      Object.entries(mapping).forEach(([colIdx, field]) => {
        if (field !== "skip") {
          (parsed as any)[field] = row[Number(colIdx)]?.trim() ?? "";
        }
      });
      return parsed;
    });
  }, [rawData, mapping]);

  // Validation summary
  const validation = useMemo(() => {
    const total = parsedRows.length;
    const noEmail = parsedRows.filter((r) => !r.email).length;
    const emails = parsedRows.filter((r) => r.email).map((r) => r.email!.toLowerCase());
    const dupeEmails = emails.filter((e, i) => emails.indexOf(e) !== i);
    return { total, noEmail, duplicatesInFile: new Set(dupeEmails).size };
  }, [parsedRows]);

  const importMutation = useMutation({
    mutationFn: async (rows: ParsedRow[]) => {
      let imported = 0;
      let skipped = 0;
      const errors: string[] = [];

      for (const row of rows) {
        if (row.email) {
          const { data: existing } = await supabase
            .from("customers")
            .select("id")
            .eq("organization_id", organizationId)
            .eq("email", row.email.toLowerCase())
            .maybeSingle();
          if (existing) { skipped++; continue; }
        }

        let categoryId: string | null = null;
        if (row.category) {
          const cat = categories.find(
            (c) => c.name.toLowerCase() === row.category!.toLowerCase() || c.slug === row.category!.toLowerCase()
          );
          categoryId = cat?.id ?? null;
        }

        const { error } = await supabase.from("customers").insert({
          organization_id: organizationId,
          full_name: row.full_name || null,
          email: row.email?.toLowerCase() || `import-${Date.now()}-${Math.random().toString(36).slice(2, 6)}@noemail.local`,
          phone: row.phone || null,
          level: row.level ? parseInt(row.level) || null : null,
          category_id: categoryId,
          notes: row.notes || null,
        });

        if (error) {
          errors.push(`Rad ${row._rowNum}: ${error.message}`);
        } else {
          imported++;
        }
      }
      return { imported, skipped, errors };
    },
    onSuccess: (result) => {
      setImportResult(result);
      setStep(3);
      qc.invalidateQueries({ queryKey: ["admin-customers"] });
    },
    onError: (err: Error) => { console.error(err); toast.error(err?.message || "Import misslyckades"); },
  });

  const previewRows = rawData.slice(0, 5);

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) { resetState(); onClose(); } }}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileSpreadsheet className="h-5 w-5" />
            Importera kunder från CSV
          </DialogTitle>
        </DialogHeader>

        {/* Step 1: Upload */}
        {step === 1 && (
          <div className="space-y-4">
            <div
              className="border-2 border-dashed rounded-xl p-8 text-center cursor-pointer hover:border-primary/50 transition-colors"
              onClick={() => fileInputRef.current?.click()}
              onDrop={handleDrop}
              onDragOver={(e) => e.preventDefault()}
            >
              <Upload className="h-10 w-10 mx-auto text-muted-foreground mb-3" />
              <p className="font-medium text-sm">Dra och släpp CSV-fil här</p>
              <p className="text-xs text-muted-foreground mt-1">eller klicka för att välja fil</p>
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv"
                className="hidden"
                onChange={(e) => handleFile(e.target.files?.[0])}
              />
            </div>

            <div className="rounded-lg bg-muted p-3 space-y-2">
              <p className="text-xs font-medium">Förväntade kolumner:</p>
              <div className="flex flex-wrap gap-1">
                {["namn", "email", "telefon", "fakturamail", "nivå", "kategori"].map((col) => (
                  <Badge key={col} variant="outline" className="text-xs">{col}</Badge>
                ))}
              </div>
              <Button variant="link" size="sm" className="h-auto p-0 text-xs" onClick={downloadTemplate}>
                <Download className="h-3 w-3 mr-1" />
                Ladda ner CSV-mall
              </Button>
            </div>
          </div>
        )}

        {/* Step 2: Preview & Map */}
        {step === 2 && (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <p className="text-sm text-muted-foreground">
                <span className="font-medium text-foreground">{fileName}</span> — {rawData.length} rader
              </p>
              <Button variant="ghost" size="sm" onClick={() => { resetState(); }}>
                Byt fil
              </Button>
            </div>

            {/* Column mapping + preview table */}
            <div className="border rounded-lg overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    {headers.map((h, i) => (
                      <TableHead key={i} className="p-1 min-w-[120px]">
                        <Select value={mapping[i] ?? "skip"} onValueChange={(v) => setMapping({ ...mapping, [i]: v })}>
                          <SelectTrigger className="h-7 text-xs">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {FIELD_OPTIONS.map((f) => (
                              <SelectItem key={f.value} value={f.value} className="text-xs">{f.label}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <p className="text-[10px] text-muted-foreground mt-0.5 truncate">{h}</p>
                      </TableHead>
                    ))}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {previewRows.map((row, ri) => (
                    <TableRow key={ri}>
                      {row.map((cell, ci) => (
                        <TableCell key={ci} className="p-1.5 text-xs truncate max-w-[150px]">
                          {cell}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
            {rawData.length > 5 && (
              <p className="text-[10px] text-muted-foreground text-center">
                Visar 5 av {rawData.length} rader
              </p>
            )}

            {/* Validation summary */}
            <div className="rounded-lg bg-muted p-3 space-y-1.5">
              <div className="flex items-center gap-1.5 text-sm">
                <CheckCircle2 className="h-4 w-4 text-emerald-600" />
                <span className="font-medium">{validation.total} rader att importera</span>
              </div>
              {validation.noEmail > 0 && (
                <div className="flex items-center gap-1.5 text-xs text-amber-600">
                  <AlertTriangle className="h-3.5 w-3.5" />
                  {validation.noEmail} rader saknar e-post (importeras ändå med genererad e-post)
                </div>
              )}
              {validation.duplicatesInFile > 0 && (
                <div className="flex items-center gap-1.5 text-xs text-destructive">
                  <XCircle className="h-3.5 w-3.5" />
                  {validation.duplicatesInFile} dubbletter i filen (alla importeras, existerande hoppar över)
                </div>
              )}
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setStep(1)}>Tillbaka</Button>
              <Button
                onClick={() => importMutation.mutate(parsedRows)}
                disabled={importMutation.isPending || !Object.values(mapping).some((v) => v !== "skip")}
              >
                {importMutation.isPending ? (
                  <><Loader2 className="h-4 w-4 animate-spin mr-1" /> Importerar...</>
                ) : (
                  `Importera ${validation.total} kunder`
                )}
              </Button>
            </DialogFooter>
          </div>
        )}

        {/* Step 3: Result */}
        {step === 3 && importResult && (
          <div className="space-y-4 py-4">
            <div className="text-center space-y-3">
              <CheckCircle2 className="h-12 w-12 mx-auto text-emerald-600" />
              <div>
                <p className="text-lg font-semibold">{importResult.imported} kunder importerade</p>
                {importResult.skipped > 0 && (
                  <p className="text-sm text-muted-foreground">{importResult.skipped} dubbletter hoppade över</p>
                )}
              </div>
            </div>

            {importResult.errors.length > 0 && (
              <div className="rounded-lg border border-destructive/30 bg-destructive/5 p-3 space-y-1">
                <p className="text-xs font-medium text-destructive">{importResult.errors.length} fel:</p>
                <div className="max-h-32 overflow-y-auto space-y-0.5">
                  {importResult.errors.map((e, i) => (
                    <p key={i} className="text-[10px] text-muted-foreground">{e}</p>
                  ))}
                </div>
              </div>
            )}

            <DialogFooter>
              <Button onClick={() => { resetState(); onClose(); }}>Stäng</Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
